import React, { useEffect, useState } from 'react';

import { Modal, Radio, Space, Tag, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { EnrollmentStatus, TrainingEnrollment } from 'models/Training';

import { LeadService } from 'services/LeadService';

import appI18n from '../../../../config/i18n';

const ALL_STATUSES: EnrollmentStatus[] = ['enrolled', 'attended', 'no_show'];

const STATUS_COLOR: Record<EnrollmentStatus, string> = {
  enrolled: 'default',
  attended: 'green',
  no_show: 'red',
};

interface Props {
  isOpen: boolean;
  trainingId: number;
  enrollment: TrainingEnrollment | null;
  handleModalCancel: () => void;
  onUpdated: (enrollmentId: number, newStatus: EnrollmentStatus) => void;
}

function UpdateEnrollmentStatusModal({
  isOpen,
  trainingId,
  enrollment,
  handleModalCancel,
  onUpdated,
}: Props) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<
    EnrollmentStatus | undefined
  >(undefined);

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(undefined);
    }
  }, [isOpen, enrollment?.enrollmentId]);

  if (!enrollment) return null;

  const targetStatuses = ALL_STATUSES.filter((s) => s !== enrollment.status);

  const handleSubmit = async () => {
    if (!selectedStatus) return;
    setSubmitting(true);
    const response = await LeadService.bulkUpdateEnrollments(trainingId, {
      enrollmentIds: [enrollment.enrollmentId],
      status: selectedStatus,
    });
    setSubmitting(false);

    if (response.success) {
      onUpdated(enrollment.enrollmentId, selectedStatus);
      handleModalCancel();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.trainings.detail.enrollment_status.errors.update_failed'),
        3.5
      );
    }
  };

  return (
    <Modal
      title={t('scenes.trainings.detail.enrollment_status.title')}
      open={isOpen}
      onCancel={handleModalCancel}
      onOk={handleSubmit}
      okText={t('scenes.trainings.detail.enrollment_status.ok')}
      cancelText={t('scenes.trainings.detail.enrollment_status.cancel')}
      okButtonProps={{ disabled: !selectedStatus, loading: submitting }}
      destroyOnClose
      width={460}
    >
      {contextHolder}
      <div style={{ fontSize: 13, marginBottom: 6 }}>
        <strong>
          {enrollment.firstName} {enrollment.lastName}
        </strong>
      </div>
      <div
        style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)', marginBottom: 16 }}
      >
        {t('scenes.trainings.detail.enrollment_status.current_label')}{' '}
        <Tag color={STATUS_COLOR[enrollment.status]} style={{ marginLeft: 4 }}>
          {t(
            `scenes.trainings.detail.enrollment_statuses.${enrollment.status}`
          )}
        </Tag>
      </div>
      <Typography.Text
        style={{ display: 'block', fontSize: 13, marginBottom: 10 }}
      >
        {t('scenes.trainings.detail.enrollment_status.select_label')}
      </Typography.Text>
      <Radio.Group
        value={selectedStatus}
        onChange={(e) => setSelectedStatus(e.target.value as EnrollmentStatus)}
        style={{ width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {targetStatuses.map((s) => (
            <Radio key={s} value={s} style={{ width: '100%' }}>
              <Tag color={STATUS_COLOR[s]} style={{ marginRight: 6 }}>
                {t(`scenes.trainings.detail.enrollment_statuses.${s}`)}
              </Tag>
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </Modal>
  );
}

export default UpdateEnrollmentStatusModal;
